import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OkAloneService } from 'src/app/ok-alone.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { CountdownConfig, CountdownEvent, CountdownComponent } from 'ngx-countdown';
import { MapOverlayTab, MapOverlayLayer } from './map-overlay.interface';
import { WeatherAlertsQuery, WeatherAlertsService } from 'src/app/services/weather-alerts.service';
import { getPointCoordinates, isPointGeometry, isPolygonGeometry } from 'src/app/pages/mgmt/threats/threat-geometry.util';
import { merge, of, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

declare var _: any;
declare var $: any;

declare var mapboxgl: any;

/** Turf.js loaded globally from index.html — used to simplify threat polygon vertices before Mapbox tessellation. */
declare var turf: any;

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LocationsComponent implements OnInit, OnDestroy {

  // workers = null;
  // center = null;
  // map = null;
  // features = [];
  // v_features = [];
  // markers = [];
  // v_markers = [];

  workers: any;
  workers_all: any;
  center: any;
  map: any;
  features: any = [];
  v_features: any = [];
  marker: any;
  markers: any = [];
  v_markers: any = [];

  settings = {
    'vehicles':true,
    'workers':true
  };

  filter:any = {
    'search': '',
    'monitor': 'all',
    'group': ''
  };

  groups_array:any = [];

  show_filter = false;

  show_all:boolean = false;

  formGroup  = new FormGroup ({
    search: new FormControl('',[]),
  });

  timer:any = null;

  config: CountdownConfig = {
    leftTime: 30,
    formatDate: ({ date }) => `${date / 1000}`,
  };

  view_color:string = 'status';
  groups:any;
  sort = "status";

  views:any = {};

  style=localStorage.getItem("map_style") ? localStorage.getItem("map_style") : "streets-v12";

  show_names=localStorage.getItem("map_show_names") ? localStorage.getItem("map_show_names") : "hidden";

  // ─── Scalable tab + overlay system ────────────────────────────────────────
  sidebarTab = 'workers';

  /** Full tab list; `tabConfig` getter hides Offenders / Threats unless the matching feature flag is on. */
  private readonly allOverlayTabs: MapOverlayTab[] = [
    { id: 'workers',   label: 'Workers',   badgeClass: 'bg-primary' },
    { id: 'offenders', label: 'Offenders', badgeClass: 'bg-warning'  },
    { id: 'threats',   label: 'Alert Zones',   badgeClass: 'bg-info'   },
  ];

  /**
   * Offenders tab and map layer: only these accounts may access when the feature flag is on.
   */
  private readonly offendersTabAllowEmails: string[] = [
    'jessica.walden@fdc.myflorida.com',
    'shawn.satterfield@fdc.myflorida.com',
  ];

  canViewOffendersTab(): boolean {
    const email = (this.okService.session?.email || '').trim().toLowerCase();
    if (!email) {
      return false;
    }
    if (this.offendersTabAllowEmails.includes(email)) {
      return true;
    }
    if (email.endsWith('@peoplesafe.co.uk') || email.endsWith('@ok-alone.com')) {
      return true;
    }
    return false;
  }

  get tabConfig(): MapOverlayTab[] {
    return this.allOverlayTabs.filter(tab => {
      if (tab.id === 'offenders' && (!this.okService.hasFeature('offenders', false) || !this.canViewOffendersTab())) {
        return false;
      }
      if (tab.id === 'threats' && !this.okService.hasFeature('threat_zones', false)) {
        return false;
      }
      return true;
    });
  }

  overlayLayers: { [id: string]: MapOverlayLayer } = {
    offenders: {
      data: [],
      visible: localStorage.getItem('map_show_offenders') === 'true',
      load:   () => this.loadOffenders(),
      setup:  () => this.setOffenderLayer(),
      remove: () => this.removeOffenderLayer(),
    },
    threats: {
      data: [],
      visible: ((): boolean => {
        const next = localStorage.getItem('map_show_threats');
        return next === 'true';
      })(),
      load:   () => this.loadWeatherAlerts(),
      setup:  () => this.setWeatherLayer(),
      remove: () => this.removeWeatherLayer(),
    }
  };

  weatherLoading = false;
  threatListFilters: { search: string; severity: string; category: string } = {
    search: '',
    severity: '',
    category: ''
  };

  /** Passed to `GET /threatzones`; empty object = API defaults (company country + 100 mi proximity). */
  threatFilters: WeatherAlertsQuery = {};

  /** Mapbox click/hover handlers for low-zoom threat centroid cluster layers. */
  private threatClusterListenersAdded = false;

  /**
   * Stable Mapbox layer listener refs — `map.off(type, layerId)` without the listener does not remove
   * layer handlers in Mapbox GL; toggling Show/Hide otherwise stacks duplicate listeners (slow map).
   */
  private readonly onThreatZonesFillClick = (e: any): void => {
    const p = e.features?.[0]?.properties;
    if (!p) {
      return;
    }
    this.openThreatZonePopup(p, e.lngLat.lng, e.lngLat.lat);
  };

  private readonly onThreatZonesFillMouseEnter = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = 'pointer';
    }
  };

  private readonly onThreatZonesFillMouseLeave = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = '';
    }
  };

  private readonly onThreatClustersClick = (e: any): void => {
    if (!this.map) {
      return;
    }
    const feats = this.map.queryRenderedFeatures(e.point, { layers: ['threat-clusters'] });
    if (!feats.length) {
      return;
    }
    const clusterId = feats[0].properties.cluster_id;
    const src = this.map.getSource('threat-points-src');
    if (!src || typeof src.getClusterExpansionZoom !== 'function') {
      return;
    }
    src.getClusterExpansionZoom(clusterId, (err: any, z: number) => {
      if (err || !this.map) {
        return;
      }
      this.map.easeTo({ center: feats[0].geometry.coordinates, zoom: z });
    });
  };

  private readonly onThreatPointsClick = (e: any): void => {
    const feat = e.features?.[0];
    if (!feat) {
      return;
    }
    const p = feat.properties;
    const coords = feat.geometry.coordinates.slice();
    this.openThreatZonePopup(p, coords[0], coords[1]);
  };

  private readonly onThreatClustersMouseEnter = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = 'pointer';
    }
  };

  private readonly onThreatClustersMouseLeave = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = '';
    }
  };

  private readonly onThreatPointsMouseEnter = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = 'pointer';
    }
  };

  private readonly onThreatPointsMouseLeave = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = '';
    }
  };

  private readonly onThreatPinsClick = (e: any): void => {
    const feat = e.features?.[0];
    if (!feat) {
      return;
    }
    const p = feat.properties;
    const coords = feat.geometry.coordinates.slice();
    this.openThreatZonePopup(p, coords[0], coords[1]);
  };

  private readonly onThreatPinsMouseEnter = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = 'pointer';
    }
  };

  private readonly onThreatPinsMouseLeave = (): void => {
    if (this.map) {
      this.map.getCanvas().style.cursor = '';
    }
  };

  /** Cache simplified GeoJSON so Hide/Show does not re-run Turf on every polygon. */
  private threatGeoCacheKey: string | null = null;
  private threatGeoCache: { centroidFeatures: any[]; polygonFeatures: any[]; pinFeatures: any[] } | null = null;
  /** Handle for the deferred prewarm so we can cancel it if data is replaced. */
  private threatGeoPrewarmHandle: any = null;
  private threatPinListenersAdded = false;

  private offenderListenersAdded = false;
  private readonly destroy$ = new Subject<void>();
  // ──────────────────────────────────────────────────────────────────────────

  subscription: any;

  last_load = 0;


  constructor(
    public okService: OkAloneService,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private weatherAlertsService: WeatherAlertsService
  ) {}

  ngOnInit() {

    if(localStorage.getItem("map_view_color")){
      this.view_color = String(localStorage.getItem("map_view_color"));
    }
    if(localStorage.getItem("map_view_sort")){
      this.sort = String(localStorage.getItem("map_view_sort"));
    }

    this.groups_array = this.okService.groups;

    if (!this.canViewOffendersTab()) {
      this.overlayLayers['offenders'].visible = false;
    }

    // Prefetch offender JSON for the sidebar list as soon as the feature is known (objects load can finish after ngOnInit).
    merge(of(undefined), this.okService.menuUpdateChange)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.okService.hasFeature('offenders', false) && this.canViewOffendersTab()) {
          this.loadOffenders();
        }
      });
    // Threat zones: fetch deferred until the map exists (processMap → map 'load') or user opens Threats tab,
    // so /home and other routes do not pay the threat payload cost when the map is not mounted.
    const tabIds = this.tabConfig.map(t => t.id);
    if (!tabIds.includes(this.sidebarTab)) {
      this.sidebarTab = tabIds[0] ?? 'workers';
    }
    this.getLocations(true);

    var _this = this;
    this.timer = setInterval(function(){
      _this.getLocations(false);
    }, 30000);


    this.subscription = this.okService.getWorkerRefreshEmitter()
    .subscribe( bypass => {
      //console.log(); 
        this.getLocations(bypass);
    } );

  }

  ngOnDestroy(): void {

    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    //console.log("Locations Destroyed");

    clearInterval(this.timer);

  }

  refreshLocations(){
    this.getLocations(false);
  }

  getLocations(ignore_limit:boolean=true){

    // Limit if done less than 5 seconds ago
    let diff = (new Date()).getTime() - this.last_load;
    let diff_limit = 5000;
    if(ignore_limit){
      diff_limit = 500
    }

    if(diff >= diff_limit){

      this.last_load = (new Date()).getTime();

      let filter = this.filter;
      filter.show_alert = 1;
      
      this.okService.get("workers/locations",filter)
      .subscribe((data) => {
        //console.log("workers",data)
        // Sort workers by priority before storing
        const workersData = (data.body || []) as any[];
        this.workers = this.sortWorkersByPriority(workersData);
        this.workers_all = this.sortWorkersByPriority(workersData);
        this.resetCountdown();
        this.processGroups();
        this.processMap();
      }

    );
    }
    //console.log("in getLocations marker", this.marker);
  }

  updateSort(){
    //console.log(this.sort);
    localStorage.setItem("map_view_sort",this.sort);
    this.getLocations(false);
  }


  processGroups(){
    this.groups = {
      'None':{
        'name':'None',
        'color':'#d1d1d1',
        'id':0,
        'workers':[],
      }
    };
    if(this.workers.length > 0){
      for (let i = 0; i < this.workers.length; i++) {

        let worker = this.workers[i];

        let group_name =  worker.group_name ? worker.group_name : 'None';
        if(!this.groups[group_name]){
          this.groups[group_name] = {
            'name':group_name,
            'color':worker.color,
            'id':worker.group_id,
            'workers':[],
          };
        }
        this.groups[group_name].workers.push(worker);

      }
      // Sort workers within each group by priority
      Object.keys(this.groups).forEach(groupName => {
        this.groups[groupName].workers = this.sortWorkersByPriority(this.groups[groupName].workers);
      });
    }
    //console.log(this.groups);
  }

  processMap(){

    let map_zoom = localStorage.getItem("map_zoom");
    let map_lat = localStorage.getItem("map_lat");
    let map_lng = localStorage.getItem("map_lng");
    let hasStoredCenter = !!(map_lat && map_lng);

    if(map_lat && map_lng){
      this.center = { loc_lat: map_lat, loc_lng: map_lng }
    }else if(this.workers.length > 0){
      this.center = { loc_lat: this.workers[0].loc_lat, loc_lng: this.workers[0].loc_lng }
    }else{
      this.center = { loc_lat: 20, loc_lng: 0 }
    }

    let zoom = hasStoredCenter ? 10 : (this.workers.length > 0 ? 10 : 2);
    if(map_zoom){
      zoom = +map_zoom;
    }

    // let worker of workers -> where if loc_lat/loc_lng lodash



    if(this.center){

      if(this.map == null){


        mapboxgl.accessToken = 'pk.eyJ1IjoidHJ1c3R5b3giLCJhIjoiY2wxY2Y0emQ1MDcwOTNwcDNxYTd3ODB1ZyJ9.moSeRr49cY5d14hosZeT1w';
        this.map = new mapboxgl.Map({
            container: 'shift_map',
            style: 'mapbox://styles/mapbox/'+this.style,
            center: [this.center.loc_lng,this.center.loc_lat],
            zoom: zoom,
        });
        this.map.addControl(new mapboxgl.FullscreenControl());
        this.map.addControl(new mapboxgl.NavigationControl());


        this.map.on('moveend', () => {
          const {lng, lat} = this.map.getCenter();
          localStorage.setItem("map_lat",lat);
          localStorage.setItem("map_lng",lng);
          localStorage.setItem("map_zoom",this.map.getZoom());
        });

        // Marker sizing once per zoom gesture (avoid per-frame querySelectorAll during zoom animation).
        this.map.on('zoomend', () => {
          this.updateMarkerSizes();
        });

        


        this.map.on('load', () => {

          this.setMarkers();
          this.map.setFog({});
          
          // Set initial marker sizes based on zoom
          this.updateMarkerSizes();

          // Defer threat-zone API fetch until the map is ready (not on ngOnInit / menu hydrate).
          if (this.okService.hasFeature('threat_zones', false)) {
            const threatLayer = this.overlayLayers['threats'];
            if (threatLayer.data.length === 0 && !this.weatherLoading) {
              this.loadWeatherAlerts();
            } else if (threatLayer.visible && threatLayer.data.length > 0) {
              this.setWeatherLayer();
            }
          }
        });

      }else{

        this.setMarkers();
        
        // Update marker sizes based on current zoom
        setTimeout(() => {
          this.updateMarkerSizes();
        }, 100);

        // Feature flag may hydrate after the first map 'load' — retry fetch when workers refresh.
        if (this.okService.hasFeature('threat_zones', false)) {
          const threatLayer = this.overlayLayers['threats'];
          if (threatLayer.data.length === 0 && !this.weatherLoading) {
            this.loadWeatherAlerts();
          }
        }

      }

  }
  }

  setMarkers(){

    if(!this.map) return;
    
    // Wait for map to be loaded before adding markers
    if(!this.map.loaded()){
      this.map.once('load', () => {
        this.setMarkers();
      });
      return;
    }

    // Remove old DOM markers
    _.forEach(this.v_markers, function(marker: any) {
      marker.remove();
    });
    _.forEach(this.markers, function(marker: any) {
      marker.remove();
    });
    this.v_features = [];
    this.features = [];
    this.markers = [];


    if(this.workers.length > 0){
      for (let i = 0; i < this.workers.length; i++) {

        let driver = this.workers[i];

        if(driver.vehicle && driver.vehicle.loc_lat){

          this.v_features.push(
            {
              'type': 'Feature',
              'geometry': {
              'type': 'Point',
              'coordinates': [
                driver.vehicle.loc_lng,driver.vehicle.loc_lat
                ]
                },
                'properties': {
                'title': driver.vehicle.name
                }
              }
          );

        }

        if(driver.loc_lat && driver.loc_lng){

          let color = this.view_color == 'status' ? this.markerColor(driver.status) : this.groupColor(driver.color);
          
          var el = this.createModernMarker(driver.name, color, driver.status);

          var marker = new mapboxgl.Marker({element: el, anchor: 'center'})
          .setLngLat([driver.loc_lng, driver.loc_lat])
          .setPopup(new mapboxgl.Popup({ offset: 25 })
          .setHTML('<p>'+driver.name+'</p>'));

          if(this.settings.workers){
            marker.addTo(this.map);
          }
          this.markers.push(marker);

          this.features.push(
            {
              'type': 'Feature',
              'geometry': {
              'type': 'Point',
              'coordinates': [
                driver.loc_lng,driver.loc_lat
                ]
                },
                'properties': {
                'title': driver.name
                }
              }
          );

        }

      }
    }

    let pointsData = { 'type': 'FeatureCollection', 'features': this.features } as any;
    let vPointsData = { 'type': 'FeatureCollection', 'features': this.v_features } as any;

    // If sources already exist, update data in-place (no flash).
    // Otherwise create sources and layers for the first time.
    if(this.map.getSource('points')){
      this.map.getSource('points').setData(pointsData);
      this.map.getSource('v_points').setData(vPointsData);
    }else{

      this.map.addSource('v_points', { 'type': 'geojson', 'data': vPointsData });

      this.map.addLayer({
        'id': 'v_points',
        'type': 'symbol',
        'source': 'v_points',
        'layout': {
        'text-field': ['get', 'title'],
        'text-font': [
          'Open Sans Semibold',
          'Arial Unicode MS Bold'
          ],
        'text-offset': [0, 1],
        'text-anchor': 'top',
        'visibility': this.show_names == 'show' ? 'visible' : 'none'
        }
      });

      this.map.addSource('points', { 'type': 'geojson', 'data': pointsData });

      this.map.addLayer({
        'id': 'points',
        'type': 'symbol',
        'source': 'points',
        'layout': {
        'text-field': ['get', 'title'],
        'text-font': [
          'Open Sans Semibold',
          'Arial Unicode MS Bold'
          ],
        'text-offset': [0, 1],
        'text-anchor': 'top',
        'visibility': (this.show_names == 'show' && this.settings.workers) ? 'visible' : 'none'
        }
      });

    }

    // Update marker sizes based on current zoom level
    setTimeout(() => {
      this.updateMarkerSizes();
    }, 50);

    this.setOffenderLayer();

  }


  // ─── Scalable overlay tab handlers ────────────────────────────────────────

  getTabCount(id: string): number {
    if (id === 'workers') return this.workers?.length ?? 0;
    return this.overlayLayers[id]?.data.length ?? 0;
  }

  /** Sidebar tab switch — ensures threats fetch runs if user opens the tab before data ever loaded. */
  selectSidebarTab(tabId: string): void {
    this.sidebarTab = tabId;
    if (
      tabId === 'threats' &&
      this.okService.hasFeature('threat_zones', false) &&
      this.overlayLayers['threats'].data.length === 0 &&
      !this.weatherLoading
    ) {
      this.loadWeatherAlerts();
    }
  }

  goToOverlayItem(coords: { lng: number; lat: number }): void {
    if (!this.map) return;
    this.map.flyTo({ center: [coords.lng, coords.lat], zoom: 14 });
  }

  /** Fly to a point and turn the overlay on if the user had hidden it from the map. */
  flyToOffenderItem(coords: { lng: number; lat: number }): void {
    const layer = this.overlayLayers['offenders'];
    if (layer && !layer.visible) {
      this.onToggleLayer('offenders');
    }
    this.goToOverlayItem(coords);
  }

  flyToThreatItem(coords: { lng: number; lat: number }): void {
    const layer = this.overlayLayers['threats'];
    if (layer && !layer.visible) {
      this.onToggleLayer('threats');
    }
    this.goToOverlayItem(coords);
  }

  onToggleLayer(id: string): void {
    if (id === 'offenders' && (!this.okService.hasFeature('offenders', false) || !this.canViewOffendersTab())) {
      return;
    }
    if (id === 'threats' && !this.okService.hasFeature('threat_zones', false)) {
      return;
    }
    const layer = this.overlayLayers[id];
    if (!layer) return;
    layer.visible = !layer.visible;
    localStorage.setItem(`map_show_${id}`, String(layer.visible));
    //console.log(`[Overlay] onToggleLayer('${id}'): visible=${layer.visible}, dataCount=${layer.data.length}`);
    if (layer.visible && layer.data.length === 0) {
      //console.log(`[Overlay] '${id}': no data yet — calling load()`);
      layer.load();
    } else {
      if (layer.visible) {
        //console.log(`[Overlay] '${id}': data exists — calling setup()`);
        layer.setup();
      } else {
        //console.log(`[Overlay] '${id}': toggled off — calling remove()`);
        layer.remove();
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────────

  // ─── Offender overlay ─────────────────────────────────────────────────────

  loadOffenders() {
    if (!this.okService.hasFeature('offenders', false) || !this.canViewOffendersTab()) {
      return;
    }
    const layer = this.overlayLayers['offenders'];
    if (layer.data.length > 0) return;
    this.http.get<any[]>('assets/offenders.json').subscribe(data => {
      layer.data = data;
      if (this.map && this.map.loaded() && layer.visible) {
        this.setOffenderLayer();
      }
    });
  }

  removeOffenderLayer() {
    if (!this.map) return;
    ['offender-points', 'offender-cluster-count', 'offender-clusters'].forEach(id => {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    });
    if (this.map.getSource('offenders-src')) this.map.removeSource('offenders-src');
  }

  setOffenderLayer() {
    if (!this.map || !this.map.loaded()) return;

    if (!this.okService.hasFeature('offenders', false) || !this.canViewOffendersTab()) {
      this.removeOffenderLayer();
      return;
    }

    this.removeOffenderLayer();

    const layer = this.overlayLayers['offenders'];
    if (!layer.visible || layer.data.length === 0) return;

    const features = layer.data
      .filter((o: any) => o.latitude != null && o.longitude != null)
      .map(o => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [o.longitude, o.latitude] },
        properties: {
          doc_num: o.doc_num,
          full_name: o.full_name,
          address: o.full_address,
          addr_type: o.addr_type,
          circuit_code: o.circuit_code
        }
      }));

    this.map.addSource('offenders-src', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
      cluster: true,
      clusterMaxZoom: 12,
      clusterRadius: 40
    });

    this.map.addLayer({
      id: 'offender-clusters',
      type: 'circle',
      source: 'offenders-src',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#f97316', 10, '#ea580c', 30, '#c2410c'],
        'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 30, 30],
        'circle-opacity': 0.85,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    });

    this.map.addLayer({
      id: 'offender-cluster-count',
      type: 'symbol',
      source: 'offenders-src',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 13
      },
      paint: { 'text-color': '#ffffff' }
    });

    this.map.addLayer({
      id: 'offender-points',
      type: 'circle',
      source: 'offenders-src',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#f97316',
        'circle-radius': 7,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.9
      }
    });

    // Only attach map event listeners once — they survive layer removal/re-addition
    if (!this.offenderListenersAdded) {
      this.offenderListenersAdded = true;

      this.map.on('click', 'offender-clusters', (e: any) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ['offender-clusters'] });
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource('offenders-src').getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
          if (err) return;
          this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
      });

      this.map.on('click', 'offender-points', (e: any) => {
        const coords = e.features[0].geometry.coordinates.slice();
        const p = e.features[0].properties;
        const transientRow = p.addr_type === 'Transient'
          ? `<p><span class="label">Type</span> <span class="addr-type-badge" style="background:#f59e0b;color:#1f2937;padding:2px 6px;border-radius:3px;font-size:12px">Transient</span></p>`
          : '';
        const html = `
          <div class="offender-popup">
            <div class="offender-popup-header">
              <strong>${p.full_name}</strong>
            </div>
            <div class="offender-popup-body">
              <p><span class="label">DOC #</span> ${p.doc_num}</p>
              <p><span class="label">Address</span> ${p.address}</p>
              ${transientRow}
              <p><span class="label">Circuit</span> ${p.circuit_code}</p>
            </div>
          </div>`;
        new mapboxgl.Popup({ maxWidth: '280px' })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(this.map);
      });

      this.map.on('mouseenter', 'offender-clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', 'offender-clusters', () => { this.map.getCanvas().style.cursor = ''; });
      this.map.on('mouseenter', 'offender-points', () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', 'offender-points', () => { this.map.getCanvas().style.cursor = ''; });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────

  // ─── Threat zones overlay (weather, Factal, custom) ─────────────────────────

  private clearThreatGeometryCache(): void {
    this.threatGeoCacheKey = null;
    this.threatGeoCache = null;
    if (this.threatGeoPrewarmHandle != null) {
      const w: any = window as any;
      if (typeof w.cancelIdleCallback === 'function') {
        try { w.cancelIdleCallback(this.threatGeoPrewarmHandle); } catch { /* noop */ }
      } else {
        clearTimeout(this.threatGeoPrewarmHandle);
      }
      this.threatGeoPrewarmHandle = null;
    }
  }

  /**
   * Build cacheKey + simplified centroid/polygon features for the current threat dataset.
   * Heavy because of turf.simplify on ~169 polygons; only call when cache is stale.
   */
  private buildThreatGeometryCache(data: any[]): { centroidFeatures: any[]; polygonFeatures: any[]; pinFeatures: any[] } {
    const centroidFeatures = data
      .filter((a: any) => {
        const c = a.centroid;
        return (
          c != null &&
          typeof c.lng === 'number' &&
          typeof c.lat === 'number' &&
          !Number.isNaN(c.lng) &&
          !Number.isNaN(c.lat)
        );
      })
      .map((a: any) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [a.centroid.lng, a.centroid.lat] },
        properties: this.threatFeatureProperties(a)
      }));

    const polygonFeatures = data
      .filter((a: any) => isPolygonGeometry(a.geometry))
      .map((a: any) => ({
        type: 'Feature',
        geometry: this.simplifyThreatGeometry(a.geometry),
        properties: this.threatFeatureProperties(a)
      }));

    const pinFeatures = data
      .filter((a: any) => a.isPin || isPointGeometry(a.geometry))
      .map((a: any) => {
        const point = getPointCoordinates(a.geometry);
        const lng = point?.[0] ?? a.centroid?.lng;
        const lat = point?.[1] ?? a.centroid?.lat;
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: this.threatFeatureProperties(a)
        };
      })
      .filter((f: any) => {
        const [lng, lat] = f.geometry.coordinates;
        return typeof lng === 'number' && typeof lat === 'number' && !Number.isNaN(lng) && !Number.isNaN(lat);
      });

    return { centroidFeatures, polygonFeatures, pinFeatures };
  }

  private threatFeatureProperties(a: any): Record<string, unknown> {
    return {
      severity:    a.severity,
      event:       a.event,
      areaDesc:    a.areaDesc,
      effective:   a.effective,
      expires:     a.expires,
      headline:    a.headline,
      description: a.description,
      category:    a.category || 'weather',
      source:      a.source || 'weather',
      isPin:       a.isPin === true
    };
  }

  private threatCacheKeyFor(data: any[]): string {
    return data.map((a: any) => String(a.id ?? '')).sort().join('|');
  }

  /**
   * Schedule the cache build during browser idle time so toggling Threats on later
   * does not pay the turf.simplify cost on the user's click (which blocks the UI).
   */
  private prewarmThreatGeometryCache(data: any[]): void {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }
    const cacheKey = this.threatCacheKeyFor(data);
    if (this.threatGeoCacheKey === cacheKey && this.threatGeoCache) {
      return;
    }
    if (this.threatGeoPrewarmHandle != null) {
      return;
    }

    const run = (): void => {
      this.threatGeoPrewarmHandle = null;
      const liveData = this.overlayLayers['threats']?.data ?? [];
      const liveKey = this.threatCacheKeyFor(liveData);
      if (liveKey !== cacheKey) {
        return;
      }
      if (this.threatGeoCacheKey === cacheKey && this.threatGeoCache) {
        return;
      }
      this.threatGeoCache = this.buildThreatGeometryCache(liveData);
      this.threatGeoCacheKey = cacheKey;
    };

    const w: any = window as any;
    if (typeof w.requestIdleCallback === 'function') {
      this.threatGeoPrewarmHandle = w.requestIdleCallback(run, { timeout: 2000 });
    } else {
      this.threatGeoPrewarmHandle = setTimeout(run, 50);
    }
  }

  loadWeatherAlerts(): void {
    if (!this.okService.hasFeature('threat_zones', false)) {
      return;
    }
    if (this.weatherLoading) {
      //console.log('[Threats] loadWeatherAlerts: already in flight, skipping duplicate call');
      return;
    }
    this.weatherLoading = true;
    //console.log('[Threats] loadWeatherAlerts: fetching from backend API...');
    this.weatherAlertsService
      .getActiveAlerts(this.threatFilters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.weatherLoading = false;
        })
      )
      .subscribe({
        next: (alerts) => {
          //console.log(`[Threats] received ${alerts.length} threat zones with geometry`);
          this.clearThreatGeometryCache();
          this.overlayLayers['threats'].data = alerts;
          if (this.map && this.overlayLayers['threats'].visible) {
            //console.log('[Threats] map exists & layer visible → calling setWeatherLayer');
            this.setWeatherLayer();
          } else {
            // Pre-warm the simplified-geometry cache during idle time so the first
            // Show on Map click does not block the UI thread on turf.simplify.
            this.prewarmThreatGeometryCache(alerts);
          }
        },
        error: (err) => {
          //console.error('[Threats] loadWeatherAlerts failed:', err);
        }
      });
  }

  onThreatFiltersChanged(filters: WeatherAlertsQuery): void {
    this.threatFilters = { ...filters };
    this.loadWeatherAlerts();
  }

  onThreatListFiltersChanged(filters: { search: string; severity: string; category: string }): void {
    this.threatListFilters = { ...filters };
    if (this.map && this.overlayLayers['threats'].visible) {
      this.setWeatherLayer();
    }
  }

  private getThreatsForMap(data: any[]): any[] {
    const severity = this.threatListFilters.severity;
    const category = this.threatListFilters.category;
    const search = (this.threatListFilters.search || '').toLowerCase().trim();

    return (data || []).filter((item: any) => {
      if (severity && item.severity !== severity) {
        return false;
      }
      if (category && item.category !== category) {
        return false;
      }
      if (search) {
        const haystack = [item.event, item.areaDesc, item.headline]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }
      return true;
    });
  }

  removeWeatherLayer(): void {
    if (!this.map) return;

    const removeLegacy = (fillId: string, outlineId: string, srcId: string): void => {
      if (this.map.getLayer(fillId)) {
        this.map.off('click', fillId);
        this.map.off('mouseenter', fillId);
        this.map.off('mouseleave', fillId);
        this.map.removeLayer(fillId);
      }
      if (this.map.getLayer(outlineId)) {
        this.map.removeLayer(outlineId);
      }
      if (this.map.getSource(srcId)) {
        this.map.removeSource(srcId);
      }
    };

    try {
      if (this.threatClusterListenersAdded) {
        this.map.off('click', 'threat-clusters', this.onThreatClustersClick);
        this.map.off('click', 'threat-points', this.onThreatPointsClick);
        this.map.off('mouseenter', 'threat-clusters', this.onThreatClustersMouseEnter);
        this.map.off('mouseleave', 'threat-clusters', this.onThreatClustersMouseLeave);
        this.map.off('mouseenter', 'threat-points', this.onThreatPointsMouseEnter);
        this.map.off('mouseleave', 'threat-points', this.onThreatPointsMouseLeave);
        this.threatClusterListenersAdded = false;
      }

      ['threat-cluster-count', 'threat-clusters', 'threat-points'].forEach((id: string) => {
        if (this.map.getLayer(id)) {
          this.map.removeLayer(id);
        }
      });
      if (this.map.getSource('threat-points-src')) {
        this.map.removeSource('threat-points-src');
      }

      if (this.threatPinListenersAdded) {
        this.map.off('click', 'threat-pins', this.onThreatPinsClick);
        this.map.off('mouseenter', 'threat-pins', this.onThreatPinsMouseEnter);
        this.map.off('mouseleave', 'threat-pins', this.onThreatPinsMouseLeave);
        this.threatPinListenersAdded = false;
      }

      if (this.map.getLayer('threat-pins')) {
        this.map.removeLayer('threat-pins');
      }
      if (this.map.getSource('threat-pins-src')) {
        this.map.removeSource('threat-pins-src');
      }

      if (this.map.getLayer('threat-zones-fill')) {
        this.map.off('click', 'threat-zones-fill', this.onThreatZonesFillClick);
        this.map.off('mouseenter', 'threat-zones-fill', this.onThreatZonesFillMouseEnter);
        this.map.off('mouseleave', 'threat-zones-fill', this.onThreatZonesFillMouseLeave);
        this.map.removeLayer('threat-zones-fill');
      }
      if (this.map.getLayer('threat-zones-outline')) {
        this.map.removeLayer('threat-zones-outline');
      }
      if (this.map.getSource('threat-zones-src')) {
        this.map.removeSource('threat-zones-src');
      }

      removeLegacy('weather-alerts-fill', 'weather-alerts-outline', 'weather-alerts-src');
    } catch (e) {
      //console.warn('[Threats] removeWeatherLayer error (non-fatal):', e);
    }
  }

  setWeatherLayer(): void {
    // map.loaded() returns false while tiles are loading (e.g. mid-pan) but
    // addSource/addLayer are safe to call as long as the style has been loaded.
    // We only gate on the map instance existing, not on map.loaded().
    if (!this.map) {
      //console.warn('[Threats] setWeatherLayer: map not initialised yet');
      return;
    }

    if (!this.okService.hasFeature('threat_zones', false)) {
      this.removeWeatherLayer();
      return;
    }

    this.removeWeatherLayer();

    const layer = this.overlayLayers['threats'];
    const mapData = this.getThreatsForMap(layer.data);
    if (!layer.visible || mapData.length === 0) {
      //console.log('[Threats] setWeatherLayer: skipping — visible:', layer.visible, 'data count:', mapData.length);
      return;
    }

    const severityColorExpr: any[] = [
      'match', ['get', 'severity'],
      'Extreme',  '#8B0000',
      'Severe',   '#FF4500',
      'Moderate', '#FFA500',
      'Minor',    '#FFD700',
      'Unknown',  '#808080',
      '#cccccc'
    ];

    const cacheKey = this.threatCacheKeyFor(mapData);

    if (this.threatGeoCacheKey !== cacheKey || !this.threatGeoCache) {
      this.threatGeoCache = this.buildThreatGeometryCache(mapData);
      this.threatGeoCacheKey = cacheKey;
    }

    const centroidFeatures = this.threatGeoCache.centroidFeatures;
    const features = this.threatGeoCache.polygonFeatures;
    const pinFeatures = this.threatGeoCache.pinFeatures;

    //console.log(`[Threats] setWeatherLayer: adding ${features.length} polygon features to map`);

    if (features.length === 0 && centroidFeatures.length === 0 && pinFeatures.length === 0) {
      //console.warn('[Threats] setWeatherLayer: no features with map geometry — nothing to render');
      return;
    }

    try {
      if (centroidFeatures.length > 0) {
        this.map.addSource('threat-points-src', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: centroidFeatures },
          cluster: true,
          clusterMaxZoom: 4,
          clusterRadius: 50
        });

        this.map.addLayer({
          id: 'threat-clusters',
          type: 'circle',
          source: 'threat-points-src',
          maxzoom: 5,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], '#fb923c', 10, '#f97316', 50, '#ea580c'],
            'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 30],
            'circle-opacity': 0.88,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });

        this.map.addLayer({
          id: 'threat-cluster-count',
          type: 'symbol',
          source: 'threat-points-src',
          maxzoom: 5,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-size': 13
          },
          paint: { 'text-color': '#ffffff' }
        });

        this.map.addLayer({
          id: 'threat-points',
          type: 'circle',
          source: 'threat-points-src',
          maxzoom: 5,
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': severityColorExpr,
            'circle-radius': 7,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.92
          }
        });
      }
    } catch (e) {
      //console.error('[Threats] setWeatherLayer: threat centroid cluster add failed:', e);
    }

    if (pinFeatures.length > 0) {
      try {
        this.map.addSource('threat-pins-src', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: pinFeatures }
        });

        this.map.addLayer({
          id: 'threat-pins',
          type: 'circle',
          source: 'threat-pins-src',
          minzoom: 5,
          paint: {
            'circle-color': severityColorExpr,
            'circle-radius': 9,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.95
          }
        });
      } catch (e) {
        //console.error('[Threats] setWeatherLayer: threat pin layer add failed:', e);
      }
    }

    if (features.length === 0) {
      this.attachThreatClusterInteractionHandlers();
      this.attachThreatPinInteractionHandlers();
      return;
    }

    try {
      this.map.addSource('threat-zones-src', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features },
        tolerance: 3,
        buffer: 0,
        generateId: true
      });
      //console.log('[Threats] setWeatherLayer: source added OK');
    } catch (e) {
      //console.error('[Threats] setWeatherLayer: addSource failed:', e);
      return;
    }

    try {
      this.map.addLayer({
        id: 'threat-zones-fill',
        type: 'fill',
        source: 'threat-zones-src',
        minzoom: 5,
        paint: {
          'fill-color': severityColorExpr,
          'fill-opacity': 0.35
        }
      });

      this.map.addLayer({
        id: 'threat-zones-outline',
        type: 'line',
        source: 'threat-zones-src',
        minzoom: 6,
        paint: {
          'line-color': severityColorExpr,
          'line-width': 2,
          'line-opacity': 0.8
        }
      });
      //console.log('[Threats] setWeatherLayer: layers added OK');
    } catch (e) {
      //console.error('[Threats] setWeatherLayer: addLayer failed:', e);
      return;
    }

    this.map.on('click', 'threat-zones-fill', this.onThreatZonesFillClick);
    this.map.on('mouseenter', 'threat-zones-fill', this.onThreatZonesFillMouseEnter);
    this.map.on('mouseleave', 'threat-zones-fill', this.onThreatZonesFillMouseLeave);

    this.attachThreatClusterInteractionHandlers();
    this.attachThreatPinInteractionHandlers();
  }

  /**
   * Reduces vertex count for Mapbox fill tessellation (Turf is optional — loaded in index.html).
   */
  private simplifyThreatGeometry(geometry: any): any {
    if (!geometry || isPointGeometry(geometry) || typeof turf === 'undefined') {
      return geometry;
    }
    try {
      const simplified = turf.simplify(geometry, { tolerance: 0.01, highQuality: false });
      return simplified != null ? simplified : geometry;
    } catch {
      return geometry;
    }
  }

  private openThreatZonePopup(p: any, lng: number, lat: number): void {
    const effective = p.effective ? new Date(p.effective).toLocaleString() : '';
    const expires   = p.expires   ? new Date(p.expires).toLocaleString()   : '';
    const description = p.description
      ? p.description.substring(0, 200) + (p.description.length > 200 ? '…' : '')
      : '';
    const categoryLabels: Record<string, string> = {
      weather: 'Weather', civil_unrest: 'Civil Unrest', heat_zone: 'Heat Zone',
      hazard: 'Hazard', custom: 'Custom', factal: 'Factal'
    };
    const sourceLabels: Record<string, string> = {
      weather: 'Weather', custom: 'Custom', factal: 'Factal'
    };
    const cat = String(p.category || 'weather').toLowerCase();
    const src = String(p.source || 'weather').toLowerCase();
    const eventLabel = this.formatThreatLabel(p.event || 'Threat');
    const catLabel = categoryLabels[cat] || this.formatThreatLabel(String(p.category || 'Threat'));
    const srcLabel = sourceLabels[src] || this.formatThreatLabel(String(p.source || ''));
    const catBg = this.threatCategoryColor(cat);
    const srcBg = this.threatSourceColor(src);
    const html = `
        <div style="max-width:280px">
          <strong>${eventLabel}</strong><br/>
          <span style="background:${this.severityColor(p.severity)};color:#fff;padding:1px 6px;border-radius:3px;font-size:11px">${p.severity}</span>
          <span style="background:${catBg};color:#fff;padding:1px 6px;border-radius:3px;font-size:11px;margin-left:4px">${catLabel}</span>
          <span style="background:${srcBg};color:#fff;padding:1px 6px;border-radius:3px;font-size:11px;margin-left:4px">${srcLabel}</span>
          <p style="margin:6px 0 2px 0"><strong>Area:</strong> ${p.areaDesc || 'N/A'}</p>
          ${effective ? `<p style="margin:2px 0"><strong>From:</strong> ${effective}</p>` : ''}
          ${expires ? `<p style="margin:2px 0"><strong>Until:</strong> ${expires}</p>` : ''}
          ${p.headline ? `<p style="margin:6px 0 2px 0;font-style:italic">${p.headline}</p>` : ''}
          ${description ? `<p style="margin:4px 0 0 0;font-size:12px">${description}</p>` : ''}
        </div>`;
    new mapboxgl.Popup({ maxWidth: '300px' })
      .setLngLat([lng, lat])
      .setHTML(html)
      .addTo(this.map);
  }

  private attachThreatClusterInteractionHandlers(): void {
    if (!this.map || this.threatClusterListenersAdded) {
      return;
    }
    if (!this.map.getLayer('threat-points')) {
      return;
    }

    this.threatClusterListenersAdded = true;

    this.map.on('click', 'threat-clusters', this.onThreatClustersClick);
    this.map.on('click', 'threat-points', this.onThreatPointsClick);
    this.map.on('mouseenter', 'threat-clusters', this.onThreatClustersMouseEnter);
    this.map.on('mouseleave', 'threat-clusters', this.onThreatClustersMouseLeave);
    this.map.on('mouseenter', 'threat-points', this.onThreatPointsMouseEnter);
    this.map.on('mouseleave', 'threat-points', this.onThreatPointsMouseLeave);
  }

  private attachThreatPinInteractionHandlers(): void {
    if (!this.map || this.threatPinListenersAdded) {
      return;
    }
    if (!this.map.getLayer('threat-pins')) {
      return;
    }

    this.threatPinListenersAdded = true;

    this.map.on('click', 'threat-pins', this.onThreatPinsClick);
    this.map.on('mouseenter', 'threat-pins', this.onThreatPinsMouseEnter);
    this.map.on('mouseleave', 'threat-pins', this.onThreatPinsMouseLeave);
  }

  private threatCategoryColor(category: string): string {
    const map: Record<string, string> = {
      weather: '#1e90ff',
      civil_unrest: '#dc3545',
      heat_zone: '#fd7e14',
      hazard: '#6f42c1',
      custom: '#6c757d',
      factal: '#0d9488'
    };
    return map[category] || '#6c757d';
  }

  private threatSourceColor(source: string): string {
    const map: Record<string, string> = {
      weather: '#1e90ff',
      custom: '#6c757d',
      factal: '#0d9488'
    };
    return map[source] || '#6c757d';
  }

  private formatThreatLabel(value: string): string {
    if (!value) {
      return '';
    }
    const uppercaseThreatWords = new Set(['osha', 'fema', 'noaa', 'nws', 'us', 'uk']);
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(part => {
        const lower = part.toLowerCase();
        if (uppercaseThreatWords.has(lower)) {
          return lower.toUpperCase();
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  private severityColor(severity: string): string {
    const map: Record<string, string> = {
      Extreme: '#8B0000', Severe: '#FF4500', Moderate: '#FFA500', Minor: '#FFD700', Unknown: '#808080'
    };
    return map[severity] || '#cccccc';
  }

  // ──────────────────────────────────────────────────────────────────────────

  markerColor(status: any){
    let color = "b5b5b5";
    if(status == "Working"){
      color = "1cd800";
    }else if(status == "Not Working"){
      color = "000000";
    }else if(status == "Help"){
      color = "ff0000";
    }else if(status == "Overdue" || status == "Man Down"){
      color = "f7921e";
    }else if(status == "mandown"){
      color = "f7921e";
    }

    return color;
  }

  groupColor(color:any){
    return color ? color.replace(/#/g, "") : 'd1d1d1';
  }

  toggleShowNames(){
    if(this.show_names == "show"){
      this.show_names = "hidden";
    }else{
      this.show_names = "show";
    }
    localStorage.setItem("map_show_names",this.show_names)

    let vis = this.show_names == 'show' ? 'visible' : 'none';
    if(this.map && this.map.getLayer('points')){
      this.map.setLayoutProperty('points', 'visibility', vis);
    }
    if(this.map && this.map.getLayer('v_points')){
      this.map.setLayoutProperty('v_points', 'visibility', vis);
    }
  }


  toggleVehicles(){

    // Disable vehicles for now
    this.settings.vehicles = false;
    return;

    this.settings.vehicles = !this.settings.vehicles;

    let self = this;
      _.forEach(this.v_markers, function(marker: any) {
        //console.log(marker);
        if(self.settings.vehicles){
          marker.addTo(self.map);
        }else{
          marker.remove();
        }

      });
      if(!self.settings.vehicles){
        this.map.setLayoutProperty('v_points', 'visibility', 'none');
      }else{
        this.map.setLayoutProperty('v_points', 'visibility', 'visible');
      }

  }

  toggleWorkers(){

    
    this.settings.workers = !this.settings.workers;

    let self = this;
      _.forEach(this.markers, function(marker: any) {
        if(self.settings.workers){
          marker.addTo(self.map);
        }else{
          marker.remove();
        }

      });
      if(this.map.getLayer('points')){
        let vis = (self.settings.workers && this.show_names == 'show') ? 'visible' : 'none';
        this.map.setLayoutProperty('points', 'visibility', vis);
      }


  }

  toggleViewColor(){
    if(this.view_color == 'status'){
      this.view_color = 'group'
    }else{
      this.view_color = 'status'
    }
    localStorage.setItem("map_view_color",this.view_color)
    this.processMap();
  }

  goToLocation(object: any){
    this.map.flyTo({
      center: [object.loc_lng,object.loc_lat],
      zoom: 14,
    });
  }



  cancelSearch(){

    this.formGroup.setValue({
      search: ''
    });
    this.workers = this.workers_all;
  }


  resetCountdown(){
    this.config = {
      leftTime: 30,
      formatDate: ({ date }) => `${date / 1000}`,
    };
  }


  toggleGroup(name:string){
    this.views[name] = !this.views[name];
  }

  changeStyle(style:string){

    this.style = style;

    localStorage.setItem("map_style",style)

    this.map.setStyle('mapbox://styles/mapbox/' + style);

    this.refreshLocations();

  }

  updateFilter(){
    this.getLocations();
  }

  clearFilter(){

    this.filter = {
      'search': '',
      'status': 'working',
      'monitor': 'all',
      'group': ''
    };
    this.getLocations();

  }

  toggleFilter(){
    this.show_filter = !this.show_filter;
  }

  /**
   * Updates marker sizes based on current zoom level
   * Markers are smaller when zoomed out and larger when zoomed in
   */
  updateMarkerSizes() {
    if (!this.map) return;
    
    const zoom = this.map.getZoom();
    const zoomThreshold = 12; // Adjust this value to change when markers grow
    
    // Get all marker elements
    const markerElements = document.querySelectorAll('.modern-marker');
    
    markerElements.forEach((marker: Element) => {
      if (zoom >= zoomThreshold) {
        marker.classList.add('large');
      } else {
        marker.classList.remove('large');
      }
    });
  }

  /**
   * Creates a modern, styled marker element with initials and color
   * @param name - Full name or vehicle name to extract initials from
   * @param colorHex - Hex color code (without #)
   * @param status - Status or type (for vehicle markers)
   * @returns HTML element ready for Mapbox marker
   */
  createModernMarker(name: string, colorHex: string, status?: string): HTMLElement {
    // Extract initials
    const words = name.split(" ");
    let initials = "";
    for (let i = 0; i < 2; i++) {
      if(words[i]){
        initials += words[i].charAt(0).toUpperCase();
      }
    }
    
    // Ensure we have at least one character
    if (!initials) {
      initials = name.charAt(0).toUpperCase();
    }

    // Create marker container
    const markerEl = document.createElement('div');
    markerEl.classList.add('modern-marker');
    
    // Create content element (the circle)
    const contentEl = document.createElement('div');
    contentEl.classList.add('marker-content');
    
    // Set background color
    const bgColor = `#${colorHex}`;
    contentEl.style.setProperty('--pin-bg-color', bgColor);
    contentEl.style.backgroundColor = bgColor;
    
    // Handle vehicle markers differently
    if (status === 'vehicle') {
      const vehicleIcon = document.createElement('i');
      vehicleIcon.classList.add('fa-solid', 'fa-truck');
      contentEl.appendChild(vehicleIcon);
    } else {
      // For worker markers, show initials
      contentEl.textContent = initials;
      
      // Calculate text color based on luminance
      const textColor = this.getContrastColor(colorHex);
      contentEl.style.color = textColor;
      
      // Add status indicator if provided
      if (status) {
        const statusIndicator = document.createElement('div');
        statusIndicator.classList.add('marker-status-indicator');
        statusIndicator.classList.add(`status-${status.toLowerCase().replace(/\s+/g, '-')}`);
        contentEl.appendChild(statusIndicator);
      }
    }
    
    markerEl.appendChild(contentEl);
    
    return markerEl;
  }

  /**
   * Calculates the best text color (black or white) based on background color luminance
   * @param hexColor - Hex color code without #
   * @returns '#000000' for light colors, '#ffffff' for dark colors
   */
  getContrastColor(hexColor: string): string {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance using WCAG formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  /**
   * Darkens a hex color by a percentage
   * @param hexColor - Hex color code without #
   * @param percent - Percentage to darken (0-1)
   * @returns Darkened hex color without #
   */
  darkenColor(hexColor: string, percent: number): string {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    const newR = Math.max(0, Math.floor(r * (1 - percent)));
    const newG = Math.max(0, Math.floor(g * (1 - percent)));
    const newB = Math.max(0, Math.floor(b * (1 - percent)));
    
    return ((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, '0');
  }

  /**
   * Calculate priority for worker sorting based on status and conditions
   * Priority 6: Fall Detection status
   * Priority 5: help status
   * Priority 4: fall_detection_active = 1 AND fall_detection_time IS NOT NULL
   * Priority 3: man_down > 0 AND last_movement conditions met
   * Priority 2: overdue status
   * Priority 1: activity or ok status
   * Priority 0: everything else
   */
  getWorkerPriority(worker: any): number {
    const currentStatus = (worker.status || '').toLowerCase();
    const statusNotEmpty = currentStatus !== '';

    // Priority 6: Fall Detection status (top priority)
    if (currentStatus === 'fall detection') {
      return 6;
    }

    // Priority 5: help status
    if (currentStatus === 'help') {
      return 5;
    }

    // Priority 4: fall_detection_active = 1 AND fall_detection_time IS NOT NULL
    if (statusNotEmpty && 
        worker.fall_detection_active === 1 && 
        worker.fall_detection_time !== null && 
        worker.fall_detection_time !== undefined) {
      return 4;
    }

    // Priority 3: man_down > 0 AND last_movement conditions
    if (statusNotEmpty && 
        worker.man_down > 0 && 
        worker.last_movement !== null && 
        worker.last_movement !== undefined) {
      // Check if last_movement is older than (man_down * 60) seconds
      const lastMovementDate = new Date(worker.last_movement);
      const now = new Date();
      const manDownSeconds = worker.man_down * 60;
      const timeDiffSeconds = (now.getTime() - lastMovementDate.getTime()) / 1000;
      
      if (timeDiffSeconds >= manDownSeconds) {
        return 3;
      }
    }

    // Priority 2: overdue status
    if (currentStatus === 'overdue') {
      return 2;
    }

    // Priority 1: activity or ok status
    if (currentStatus === 'activity' || currentStatus === 'ok' || currentStatus === 'working') {
      return 1;
    }

    // Priority 0: everything else
    return 0;
  }

  /**
   * Sort workers by priority (highest first), then by name
   */
  sortWorkersByPriority(workers: any[]): any[] {
    return [...workers].sort((a, b) => {
      const priorityA = this.getWorkerPriority(a);
      const priorityB = this.getWorkerPriority(b);
      
      // Sort by priority (descending - highest first)
      if (priorityB !== priorityA) {
        return priorityB - priorityA;
      }
      
      // If same priority, sort by name (ascending)
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  /**
   * Get status badge information with color and styling
   */
  getStatusBadge(status: string): { class: string, text: string, color: string } {
    const normalizedStatus = (status || '').toLowerCase();
    
    if (normalizedStatus === 'ok' || normalizedStatus === 'working') {
      return { class: 'bg-success', text: 'OK', color: '#28a745' };
    }
    if (normalizedStatus === 'help') {
      return { class: 'bg-danger', text: 'Help', color: '#ff0000' };
    }
    if (normalizedStatus === 'fall detection') {
      return { class: 'bg-danger', text: 'Fall Detection', color: '#ff0000' };
    }
    if (normalizedStatus === 'overdue') {
      return { class: 'bg-warning', text: 'Overdue', color: '#f7921e' };
    }
    if (normalizedStatus === 'mandown' || normalizedStatus === 'man down') {
      return { class: 'bg-warning', text: 'Man Down', color: '#f7921e' };
    }
    if (normalizedStatus === '' || normalizedStatus === 'not working') {
      return { class: 'bg-secondary', text: 'Not Working', color: '#000000' };
    }
    return { class: 'bg-light text-dark', text: status || 'Unknown', color: '#b5b5b5' };
  }

  /**
   * Toggle worker details visibility
   */
  toggleWorkerDetails(workerNum: number): void {
    if (!this.views['worker_' + workerNum]) {
      this.views['worker_' + workerNum] = false;
    }
    this.views['worker_' + workerNum] = !this.views['worker_' + workerNum];
  }

  /**
   * Check if any worker in the group has an alert
   */
  groupHasAlert(workers: any[]): boolean {
    return workers.some(worker => worker.alert);
  }
}
